import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { AuthService, User } from '@isophi/core-legacy';
import { ToastService } from '@isophi/core-ui';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs/operators';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { AuthApiService } from '../../../../core/services/auth-api.service';
import { HelperService } from '../../../../core/services/helper.service';
import { ChangePassword } from '../../models/change-password.model';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChangePasswordComponent {
  public changePassword: ChangePassword = new ChangePassword();

  public disabledButton = false;

  public passwordChanged = false;

  public formError: string | null = null;

  public oldPasswordError: string | null = null;

  public newPasswordError: string | null = null;

  public newPasswordConfirmError: string | null = null;

  public user: User;

  public constructor(
    public links: RouterLinkFactory,
    private spinner: NgxSpinnerService,
    private authApiService: AuthApiService,
    private toastr: ToastService,
    private cdRef: ChangeDetectorRef,
    private authService: AuthService,
    private helperService: HelperService,
    private translocoService: TranslocoService
  ) {
    this.user = this.authService.loggedUser;
    this.helperService.setTitle(null, this.translocoService.translate('user/passwordChange'));
  }

  /**
   * Function to process change password form, when submitted.
   *
   * todo(doubravskytomas): Should be logic (http communication) refactored into service?
   */
  public onSubmit(): void {
    this.startProcessingForm();

    if (!this.changePassword.checkPasswordConfirm()) {
      this.newPasswordConfirmError = this.translocoService.translate('user/newPasswordConfirmError');
      this.finishProcessingForm();
      return;
    }

    this.authApiService
      .changePassword(this.getChangePasswordParams())
      .pipe(finalize(() => this.finishProcessingForm()))
      .subscribe({
        next: () => {
          this.passwordChanged = true;
          this.changePassword.clear();
          this.toastr.success(this.translocoService.translate('user/changePasswordSuccessToast'));
          this.cdRef.detectChanges();
        },
        error: (error) => {
          const errorData = error.error;

          if (errorData.errors === undefined || error.status === 403) {
            this.formError = this.translocoService.translate('user/changePasswordErrorToast');
            this.toastr.error(this.formError);
            return;
          }

          for (const errorItem of errorData.errors) {
            if (errorItem.field === 'old_password') {
              this.oldPasswordError = errorItem.message;
            }
            if (errorItem.field === 'new_password1') {
              this.newPasswordError = errorItem.message;
            }
            if (errorItem.field === 'new_password2') {
              this.newPasswordConfirmError = errorItem.message;
            }
          }
          this.cdRef.detectChanges();
        },
      });
  }

  /**
   * Start processing form.
   */
  protected startProcessingForm(): void {
    this.disabledButton = true;
    this.clearFormErrors();
    this.spinner.show();
  }

  /**
   * Finish processing form.
   */
  protected finishProcessingForm(): void {
    this.spinner.hide();
    this.disabledButton = false;
  }

  /**
   * Clear all errors in form.
   */
  protected clearFormErrors(): void {
    this.formError = null;
    this.newPasswordConfirmError = null;
    this.newPasswordError = null;
    this.oldPasswordError = null;
  }

  /**
   * Return parameters for change password request.
   */
  protected getChangePasswordParams(): { [key: string]: string } {
    return {
      old_password: this.changePassword.oldPassword,
      new_password1: this.changePassword.newPassword,
      new_password2: this.changePassword.newPasswordConfirm,
    };
  }
}
